import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ThumbsUp, ThumbsDown, LifeBuoy, PlayCircle } from "lucide-react";
import { MarkdownLite, ArticleBody, ContactSupportDialog, plain } from "@/components/help/helpShared";
import { LinkedKnowledgeFiles } from "@/components/knowledge/KnowledgeFilePicker";
import { toast } from "sonner";
import { RelatedByTopic } from "@/components/knowledge/Topics";

function TrainingVideo({ video }) {
  if (!video || !video.url || video.published === false) return null;
  const url = video.url;
  const isEmbed = /youtube\.com|youtu\.be|vimeo\.com|\/embed\//i.test(url);
  return (
    <div className="my-6 rounded-xl border border-slate-200 overflow-hidden bg-slate-900" data-testid="help-article-video">
      <div className="px-4 py-2.5 bg-slate-800 text-white flex items-center gap-2">
        <PlayCircle className="w-4 h-4 text-brand-cyan" />
        <span className="text-sm font-medium" data-testid="help-video-title">{video.title || "Training Video"}</span>
        {video.duration && <span className="text-xs text-white/50 ml-auto">{video.duration}</span>}
      </div>
      <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
        {isEmbed ? (
          <iframe src={url} title={video.title || "Training video"} className="absolute inset-0 w-full h-full"
            allow="accelerated-download; encrypted-media; picture-in-picture" allowFullScreen data-testid="help-video-iframe" />
        ) : (
          <video controls poster={video.thumbnail_url || undefined} className="absolute inset-0 w-full h-full bg-black" data-testid="help-video-player">
            <source src={url} />
          </video>
        )}
      </div>
      {video.transcript && (
        <details className="bg-white border-t border-slate-200" data-testid="help-video-transcript">
          <summary className="px-4 py-2 text-sm font-medium text-slate-700 cursor-pointer select-none">Read transcript</summary>
          <div className="px-4 pb-4 text-sm text-slate-600 whitespace-pre-wrap">{video.transcript}</div>
        </details>
      )}
    </div>
  );
}

export default function HelpArticle({ slug: slugProp, onBack, onOpenArticle } = {}) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const back = () => onBack ? onBack() : navigate("/help");
  const openArticle = (s) => onOpenArticle ? onOpenArticle(s) : navigate(`/help/article/${s}`);
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [support, setSupport] = useState(false);

  useEffect(() => {
    setA(null); setNotFound(false); setFeedback(null);
    api.get(`/help/articles/${slug}`).then((r) => setA(r.data)).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true); else toast.error(apiError(e));
    });
  }, [slug]);

  const sendFeedback = async (helpful) => {
    setFeedback(helpful ? "yes" : "no");
    try { await api.post(`/help/articles/${a.id}/feedback`, { helpful }); toast.success("Thanks for your feedback!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (notFound) return (
    <div className="max-w-3xl mx-auto text-center py-16" data-testid="help-article-notfound">
      <p className="text-slate-500">This article isn't available for your account or role.</p>
      <Button variant="outline" className="mt-4" onClick={back}>Back to Help Center</Button>
    </div>
  );
  if (!a) return <div className="max-w-3xl mx-auto h-64 animate-pulse rounded-xl bg-slate-50" />;

  return (
    <div className="max-w-3xl mx-auto" data-testid="help-article">
      <button onClick={back} className="text-sm text-slate-500 hover:text-brand flex items-center gap-1 mb-4" data-testid="help-article-back">
        <ChevronLeft className="w-4 h-4" />Help Center
      </button>
      <p className="text-xs font-medium text-brand uppercase tracking-wide">{(a.category || "").replace(/-/g, " ")}</p>
      <h1 className="font-display text-2xl font-bold text-slate-900 mt-1" data-testid="help-article-title">{a.title}</h1>
      {a.summary && <p className="text-slate-500 mt-2">{plain(a.summary)}</p>}
      {a.status === "coming_soon" && <span className="inline-block mt-2 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1" data-testid="help-coming-soon">Coming Soon — documentation for this feature is being prepared.</span>}
      {a.updated_at && <p className="text-xs text-slate-400 mt-1">Last updated {new Date(a.updated_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</p>}
      <div className="border-t border-slate-100 my-5" />
      {(a.video?.position !== "after") && <TrainingVideo video={a.video} />}
      <ArticleBody article={a} onOpenArticle={openArticle} />
      {(a.video?.position === "after") && <TrainingVideo video={a.video} />}
      <LinkedKnowledgeFiles files={a.kc_files || []} />

      <RelatedByTopic topicIds={a.topic_ids || []} exclude={a.id} show={["training"]} />
      {a.related?.length > 0 && (
        <div className="mt-8" data-testid="help-article-related">
          <h3 className="font-semibold text-slate-900 mb-2">Related articles</h3>
          <div className="space-y-1.5">
            {a.related.map((r) => <button key={r.slug} onClick={() => openArticle(r.slug)} className="block text-sm text-brand hover:underline">{r.title}</button>)}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 mt-8 flex flex-wrap items-center justify-between gap-3" data-testid="help-feedback">
        <div>
          <p className="font-medium text-slate-800 text-sm">Was this helpful?</p>
          {feedback && <p className="text-xs text-slate-500 mt-0.5">Thanks for letting us know.</p>}
        </div>
        {!feedback ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => sendFeedback(true)} data-testid="feedback-yes"><ThumbsUp className="w-4 h-4 mr-1.5" />Yes</Button>
            <Button variant="outline" size="sm" onClick={() => sendFeedback(false)} data-testid="feedback-no"><ThumbsDown className="w-4 h-4 mr-1.5" />No</Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setSupport(true)} data-testid="help-article-support"><LifeBuoy className="w-4 h-4 mr-1.5" />Still need help?</Button>
        )}
      </div>
      <ContactSupportDialog open={support} onClose={() => setSupport(false)} articleSlug={a.slug} />
    </div>
  );
}
